import { useCallback, useEffect, useState } from 'react';
import { getApiClient } from '@signalvote/api-client';
import { readDraftImage, writeDraftImage } from './expressDraftImage';
import { SEND_STEP_ORDER, type SendStepKey } from './expressSteps';

export { readDraftImage, writeDraftImage };

/**
 * What the Express flow collects before the account exists, held on this device. Account answers (the
 * password above all) are never held here. The text's image is too big for this record and waits in
 * IndexedDB instead (expressDraftImage.ts).
 */
export interface ExpressDraft {
  message: ExpressMessageDraft | null;
  /** What the person is running for; null until they answer, and either part may be left blank. */
  race: ExpressRaceDraft | null;
}

export interface ExpressRaceDraft {
  office: string;
  /** A two-letter state, or null for "Not sure yet". */
  state: string | null;
}

export interface ExpressMessageDraft {
  body: string;
  /** Absent until the person types one; the composer shows its prefill until then. */
  paidForDisclaimer?: string;
}

export const EMPTY_EXPRESS_DRAFT: ExpressDraft = { message: null, race: null };

const KEY = 'express-draft';

export function readExpressDraft(): ExpressDraft {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? { ...EMPTY_EXPRESS_DRAFT, ...(JSON.parse(raw) as Partial<ExpressDraft>) } : EMPTY_EXPRESS_DRAFT;
  } catch {
    return EMPTY_EXPRESS_DRAFT;
  }
}

function writeExpressDraft(draft: ExpressDraft) {
  try {
    localStorage.setItem(KEY, JSON.stringify(draft));
  } catch {
    // Storage off (private window): the draft lives only as long as the page.
  }
}

export function clearExpressDraft() {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // Nothing was stored.
  }
}

const REACHED_KEY = 'express-reached';

interface ReachedStep {
  workOrderId: string;
  step: SendStepKey;
}

/**
 * The furthest send step the person got to on this device for this Draft, or null when this device
 * hasn't seen it. The server can tell which steps a Draft answers but not which ones the person
 * pressed on from, so reopening the link would otherwise land past where they stopped.
 */
export function readReachedStep(workOrderId: string): SendStepKey | null {
  try {
    const raw = localStorage.getItem(REACHED_KEY);
    const reached = raw ? (JSON.parse(raw) as ReachedStep) : null;
    return reached?.workOrderId === workOrderId && SEND_STEP_ORDER.includes(reached.step) ? reached.step : null;
  } catch {
    return null;
  }
}

/** Records `step` for this Draft unless the person already got further; going back keeps the furthest. */
export function recordReachedStep(workOrderId: string, step: SendStepKey) {
  const reached = readReachedStep(workOrderId);
  if (reached && SEND_STEP_ORDER.indexOf(reached) >= SEND_STEP_ORDER.indexOf(step)) return;
  try {
    localStorage.setItem(REACHED_KEY, JSON.stringify({ workOrderId, step } satisfies ReachedStep));
  } catch {
    // Storage off: reopening falls back to the first step the Draft doesn't answer.
  }
}

export type SetExpressDraft = (update: (draft: ExpressDraft) => ExpressDraft) => void;

/** The device-held draft, written back on every change so a reload keeps it. */
export function useExpressDraft(): { draft: ExpressDraft; setDraft: SetExpressDraft } {
  const [draft, setState] = useState<ExpressDraft>(readExpressDraft);
  const setDraft = useCallback<SetExpressDraft>((update) => {
    setState((prev) => {
      const next = update(prev);
      writeExpressDraft(next);
      return next;
    });
  }, []);
  return { draft, setDraft };
}

/** The draft's image, read from the device once and written back on every change. */
export function useDraftImage(): { image: File | null; setImage: (file: File | null) => void } {
  const [image, setState] = useState<File | null>(null);
  useEffect(() => {
    let current = true;
    void readDraftImage().then((file) => current && setState(file));
    return () => {
      current = false;
    };
  }, []);
  const setImage = useCallback((file: File | null) => {
    setState(file);
    void writeDraftImage(file);
  }, []);
  return { image, setImage };
}

/**
 * Runs once, right after sign-up created the campaign and selected it: hands the device-held draft to
 * the campaign as its Draft Text send, with the image. Resolves to null when everything is saved, so the
 * device copy is cleared; or to the step to answer again, and the device copy is kept for it. A failed
 * create throws with nothing saved. When only the image fails, the text is saved and cleared here and
 * the image stays on the device: the message step uploads it to the Draft it finds.
 */
export async function saveDraftAfterSignup(draft: ExpressDraft, _campaignId: string): Promise<SendStepKey | null> {
  if (!draft.message) return 'message';
  const api = getApiClient();
  const image = await readDraftImage();
  const created = await api.workOrders.create({
    amount: null,
    messageText: draft.message.body,
    paidForDisclaimer: draft.message.paidForDisclaimer ?? null,
    sendDate: null,
    sendTime: null,
    keepOneUnpaidTextSend: true,
  });
  clearExpressDraft();
  if (!image) return null;
  try {
    await api.workOrders.attachImage(created.id, image, created.concurrencyVersion);
  } catch {
    return 'message';
  }
  await writeDraftImage(null);
  return null;
}
